const DEFAULT_ROUTE_PARSER_BASE = 'http://localhost';

export const VALID_HANDOFFS = new Set(['cloud', 'datahub', 'lantern-v3']);

const normalizeReturnTo = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed || null;
};

const getHandoffParamsFromSearch = (search) => {
  const params = new URLSearchParams(typeof search === 'string' ? search : '');
  const handoff = params.get('handoff');
  return {
    handoff: VALID_HANDOFFS.has(handoff) ? handoff : null,
    returnTo: normalizeReturnTo(params.get('returnTo')),
  };
};

export const getPreservedPath = (location) => {
  const from = location?.state?.from;
  if (typeof from !== 'string') {
    return null;
  }
  if (!from.startsWith('/')) {
    return null;
  }
  return from;
};

const getHandoffParamsFromPreservedPath = (location) => {
  const preservedPath = getPreservedPath(location);
  if (!preservedPath) {
    return { handoff: null, returnTo: null };
  }

  try {
    const preservedUrl = new URL(preservedPath, DEFAULT_ROUTE_PARSER_BASE);
    return getHandoffParamsFromSearch(preservedUrl.search);
  } catch (error) {
    return { handoff: null, returnTo: null };
  }
};

export const getHandoffParamsFromLocation = (location) => {
  const directParams = getHandoffParamsFromSearch(location?.search);
  const preservedParams = getHandoffParamsFromPreservedPath(location);

  return {
    handoff: directParams.handoff ?? preservedParams.handoff,
    returnTo: directParams.returnTo ?? preservedParams.returnTo,
  };
};

export const getHandoffFromLocation = (location) => getHandoffParamsFromLocation(location).handoff;

export const getPortalHandoff = (search) => getHandoffParamsFromSearch(search).handoff;

export const sanitizeLanternReturnRoute = (candidate) => {
  const normalizedCandidate = normalizeReturnTo(candidate);
  if (!normalizedCandidate || !normalizedCandidate.startsWith('/')) {
    return null;
  }

  try {
    const candidateUrl = new URL(normalizedCandidate, DEFAULT_ROUTE_PARSER_BASE);
    if (candidateUrl.origin !== DEFAULT_ROUTE_PARSER_BASE) {
      return null;
    }
    if (!candidateUrl.pathname.startsWith('/')) {
      return null;
    }
    return `${candidateUrl.pathname}${candidateUrl.search}`;
  } catch (error) {
    return null;
  }
};

export const getSafeReturnToFromLocation = (location) =>
  sanitizeLanternReturnRoute(getHandoffParamsFromLocation(location).returnTo);

export const getSafeReturnToFromSearch = (search) =>
  sanitizeLanternReturnRoute(getHandoffParamsFromSearch(search).returnTo);

export const resolveLanternHandoffPath = ({ handoff, returnTo }) => {
  if (!VALID_HANDOFFS.has(handoff)) {
    return null;
  }

  const safeReturnTo = sanitizeLanternReturnRoute(returnTo);
  if (safeReturnTo) {
    return safeReturnTo;
  }

  return handoff === 'datahub' || handoff === 'cloud' ? '/datahub' : '/';
};

export const buildLanternHandoffUrl = ({
  lanternBase,
  handoff,
  accessToken,
  refreshToken,
  returnTo = null,
}) => {
  if (!lanternBase || !VALID_HANDOFFS.has(handoff) || !accessToken) {
    return null;
  }

  const targetPath = resolveLanternHandoffPath({ handoff, returnTo });
  if (!targetPath) {
    return null;
  }

  const fragmentPayload = {
    a: accessToken,
    r: refreshToken,
    ts: Date.now(),
  };
  const fragment = `#df8=${btoa(JSON.stringify(fragmentPayload))}`;
  return `${lanternBase}${targetPath}${fragment}`;
};
